// The form button (FR-016, FR-047; research R14): 44 px, radius 12, `pr` fill with white Poppins 600
// 14 px, or the outline variant. While `pending` it shows progress and cannot be pressed again.
import { ActivityIndicator, StyleSheet } from "react-native";

import { makeStyles } from "@/theme/makeStyles";
import { useTheme } from "@/theme/useTheme";

import { PressableTarget, type PressableTargetProps } from "./PressableTarget";
import { Text } from "./Text";

export type ButtonProps = Omit<
  PressableTargetProps,
  "children" | "style" | "accessibilityLabel" | "onPress"
> & {
  children: string;
  /** Optional when a parent (expo-router `Link asChild`) supplies it. */
  onPress?: PressableTargetProps["onPress"];
  variant?: "primary" | "outline";
  pending?: boolean;
  disabled?: boolean;
  testID?: string;
};

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    base: {
      minHeight: t.size.button,
      borderRadius: t.radius.button,
      paddingHorizontal: t.space.lg,
      flexDirection: "row",
      gap: t.space.sm,
      alignSelf: "stretch",
    },
    primary: { backgroundColor: t.color.primary },
    outline: {
      backgroundColor: t.color.surface,
      borderWidth: t.borderWidth.input,
      borderColor: t.color.border,
    },
    disabled: { opacity: 0.6 },
    primaryText: { color: t.onPrimary },
  }),
);

export function Button({
  children,
  onPress,
  variant = "primary",
  pending = false,
  disabled = false,
  testID,
  ...rest
}: ButtonProps) {
  const styles = useStyles();
  const theme = useTheme();
  const inactive = pending || disabled;
  return (
    <PressableTarget
      {...rest}
      accessibilityLabel={children}
      accessibilityState={{ disabled: inactive, busy: pending }}
      aria-busy={pending}
      disabled={inactive}
      onPress={onPress}
      style={[styles.base, styles[variant], inactive && styles.disabled]}
      testID={testID}
    >
      {pending ? (
        <ActivityIndicator
          size="small"
          color={variant === "primary" ? theme.onPrimary : theme.color.text}
        />
      ) : null}
      <Text
        variant="heading"
        weight="semiBold"
        size="base"
        style={variant === "primary" ? styles.primaryText : undefined}
      >
        {children}
      </Text>
    </PressableTarget>
  );
}
